<template>
  <v-app>
    <a href="#main" class="skip-to-main-content-link">Skip to main content</a>
    <!-- Declared before <v-app-bar> so it sits on top of everything -->
    <InboxDrawer></InboxDrawer>
    <v-system-bar v-if="isStaging" color="orange" height="30">
      <div class="text-center font-weight-bold" style="width:100%;color:#000;">
        You are viewing the Uwill Test Application
      </div>
    </v-system-bar>
    <v-system-bar v-if="isDemo" color="#6667AB" height="30">
      <div class="text-center font-weight-bold" style="width:100%;color:#000;">
        You are viewing the Uwill Demo Application
      </div>
    </v-system-bar>
    <ucollaborate-version-banner :available="showUcollaborateBanner" />
    <v-app-bar
      v-if="$route.name != 'Demo' && $route.name != 'EventRegistration'"
      class="uwill app-bar"
      :class="{ 'is-redesign': isCollaborate && !isLegacyUcollaborateView }"
      border
      elevation="0"
    >
      <a href="/">
        <div class="d-flex align-center brand ml-3">
          <img
            class="logo"
            alt="Uwill Dashboard Home Logo"
            :src="images.uwill"
          />
        </div>
      </a>
      <portal-target name="app-bar-start" />
      <v-spacer></v-spacer>
      <v-chip
        v-if="is_impersonating"
        class="ma-2"
        color="#ff0000"
        size="large"
        link
        @click="$methods.logout"
      >
        You are impersonating another user
      </v-chip>
      <InboxLauncher v-if="can_message"></InboxLauncher>
      <span v-if="isLoggedIn" class="welcome">
        Welcome, {{ user.firstName }}
      </span>
      <span v-else>
        <a href="/login" class="mr-3">Log In</a>
      </span>
      <v-menu
        v-model="account_menu"
        origin="top left"
        location="bottom"
        :persistent="false"
        close-on-content-click
        content-class="account-menu"
        :retain-focus="false"
        min-width="225px"
        @after-enter="focusFirstMenuItem"
      >
        <template #activator="{ props }">
          <v-btn icon v-bind="props" aria-label="Account Information">
            <v-icon class="uwill-primary">fa-solid fa-circle-user</v-icon>
          </v-btn>
        </template>
        <v-list role="menu" aria-label="Main Menu">
          <v-list-item
            class="ada-menu-item"
            role="menuitem"
            tag="a"
            @click="$methods.home"
            >Home</v-list-item
          >
          <v-list-item
            v-if="user && user.provider && !isUriseOnly"
            target="NEW"
            href="https://uwill.com/wp-content/uploads/2023/08/uwill_counselors_quick_start_guide_2023_FNL.pdf"
            role="menuitem"
            style="color: rgba(0, 0, 0, 0.87);"
          >
            {{ $filters.capitalize($t("text.provider")) }} Guide
          </v-list-item>
          <v-list-item
            v-if="
              user &&
                !isUriseOnly &&
                (isCollaborate || (!user.provider && user.education))
            "
            target="NEW"
            href="https://uwill.com/wp-content/uploads/2020/10/uwill_student_quick_start_guide_10.20.pdf"
            role="menuitem"
            style="color: rgba(0, 0, 0, 0.87);"
          >
            {{ $filters.capitalize($methods.getStudentWord()) }} Guide
          </v-list-item>
          <v-list-item
            v-else-if="!isUriseOnly"
            target="NEW"
            href="https://uwill.com/wp-content/uploads/2020/10/uwill_student_quick_start_guide_10.20.pdf"
            role="menuitem"
            style="color: rgba(0, 0, 0, 0.87);"
          >
            Guide
          </v-list-item>
          <v-list-item
            v-if="!isCollaborate && !isUriseOnly"
            @click="this.$refs.promo_code_dialog.openDialog()"
          >
            Redeem A Promo Code
          </v-list-item>
          <v-list-item
            v-if="
              user &&
                !isUriseOnly &&
                (user.provider || user.contractor) &&
                user.payoutSetupComplete
            "
            target="NEW"
            role="menuitem"
            @click="$methods.getStripeLink()"
          >
            Manage your Stripe Payment Information
          </v-list-item>
          <v-list-item
            v-if="
              user &&
                !isUriseOnly &&
                !isCollaborate &&
                !isOperator &&
                !user.provider
            "
            href="/purchasecredits"
            role="menuitem"
            style="color: rgba(0, 0, 0, 0.87);"
          >
            Purchase Credits
          </v-list-item>
          <v-divider v-if="isLoggedIn"></v-divider>
          <v-list-item
            v-if="
              user && isLoggedIn && user.tfaSecret && !$store.getters.needsTFA
            "
            role="menuitem"
            @click="$methods.remove2FA()"
          >
            Remove Two-Factor Authentication
          </v-list-item>
          <v-list-item
            v-else-if="user && isLoggedIn && !user.tfaSecret"
            href="/2fa"
            role="menuitem"
            style="color: rgba(0, 0, 0, 0.87);"
          >
            Set Up Two-Factor Authentication
          </v-list-item>
          <v-list-item v-if="!isLoggedIn" @click="login">Log in</v-list-item>
          <v-list-item v-else @click="logout">Log out</v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <PromoCode
      v-if="!isCollaborate && !isUriseOnly"
      ref="promo_code_dialog"
      role="menuitem"
      @close-menu="$methods.closeMenu"
    ></PromoCode>

    <v-main id="main" class="uwill-body" role="main">
      <portal-target name="nav-items" />
      <flash-message />
      <show-error></show-error>
      <router-view
        v-if="
          (!isLoggedIn ||
            isConfirmed ||
            isConfirming ||
            isGuardianConsenting) &&
            !isIncomplete
        "
        class="router-view"
      ></router-view>
      <CompleteProfile
        v-else-if="isIncomplete"
        :user-telephone="user.profile.telephone"
        :user-birthdate="user.birthdate"
        :is-student="user.education"
      ></CompleteProfile>
      <Confirmation v-else :is-student="!user.provider"></Confirmation>
    </v-main>

    <v-footer>
      <v-col class="text-center text-caption" cols="12">
        &copy; {{ new Date().getFullYear() }} <strong>Uwill, Inc.</strong> |
        <a href="/doc/privacy" class="font-weight-bold" alt="Privacy Policy"
          >Privacy Policy</a
        >
        |
        <a
          href="/doc/terms"
          class="font-weight-bold"
          alt="Terms and Conditions of Use"
          >Terms and Conditions of Use</a
        >
        |
        <strong>
          For assistance, email us at
          <a
            href="mailto:support@uwill.com"
            class="font-weight-bold"
            alt="Support Email Address"
            >support@uwill.com</a
          >
        </strong>
      </v-col>
    </v-footer>
  </v-app>
</template>
<script>
import Confirmation from "@/components/Confirmation.vue";
import CompleteProfile from "@/components/CompleteProfile.vue";
import PromoCode from "@/components/PromoCode.vue";
import FlashMessage from "@/components/FlashMessage.vue";
import UcollaborateVersionBanner from "@/components/collaboration/UcollaborateVersionBanner.vue";
import { onMounted } from "vue";
import tokenService from "@/utils/tokenService";
import { identifyAmplitudeUser } from "@/utils/amplitudeIdentity";
import InboxLauncher from "@/components/messaging/InboxLauncher.vue";
import InboxDrawer from "@/components/messaging/InboxDrawer.vue";
import useInboxState from "@/composables/useInboxState";

const inbox = useInboxState();

export default {
  name: "App",
  components: {
    Confirmation,
    PromoCode,
    CompleteProfile,
    FlashMessage,
    UcollaborateVersionBanner,
    InboxLauncher,
    InboxDrawer
  },
  setup() {
    onMounted(async () => {
      const response = await fetch(
        `${process.env.VUE_APP_BASE_API_URL}/regionbyip`
      );
      const data = await response.json();
      if (
        [
          "United Kingdom",
          "Alberta",
          "British Columbia",
          "Manitoba",
          "New Brunswick",
          "Newfoundland and Labrador",
          "Nova Scotia",
          "Ontario",
          "Prince Edward Island",
          "Quebec",
          "Saskatchewan"
        ].includes(data.region)
      ) {
        const script = document.createElement("script");

        script.src = "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js";
        script.type = "text/javascript";
        script.charset = "UTF-8";
        script.setAttribute(
          "data-domain-script",
          "0196cb3c-fe84-7bf6-80c2-84ff610829b8-test"
        );

        script.onload = () => {
          // Optional: the OptanonWrapper function can go here if needed
          window.OptanonWrapper = function() {
            // Your custom logic here, if needed
          };
        };

        document.head.appendChild(script);
      }
    });
  },
  data: () => ({
    metaInfo: {
      title: "Uwill",
      // all titles will be injected into this template
      titleTemplate: "%s | Uwill"
    },
    account_menu: false,
    images: {
      uwill: require("@/assets/images/uwill.png")
    }
  }),
  computed: {
    isLoggedIn: function() {
      return this.$store.getters.isLoggedIn;
    },
    amplitudeIdentityKey: function() {
      const user = this.$store.state.user;
      if (!this.isLoggedIn || user?.id == null) {
        return null;
      }
      return JSON.stringify([
        user.id,
        user.organizationNames || "",
        this.isUriseOnly,
        this.isUriseOnly ? null : user.provider
      ]);
    },
    isConfirmed: function() {
      return this.$store.state.user.confirmed;
    },
    isDashboard: function() {
      return this.$store.getters.isDashboard;
    },
    isConfirming: function() {
      return this.$route.path.indexOf("/confirm/") !== -1;
    },
    isIncomplete: function() {
      return !this.isUriseOnly && this.$store.state.user.profileIncomplete;
    },
    isUriseOnly: function() {
      return this.$store.getters.isUriseOnly;
    },
    isGuardianConsenting: function() {
      return this.$route.path.indexOf("/guardianconsent/") !== -1;
    },
    isCollaborate: function() {
      return this.$store.getters.isCollaborate;
    },
    isLegacyUcollaborateView: function() {
      return this.$store.getters.isLegacyUcollaborateView;
    },
    isOperator: function() {
      return this.$store.getters.isOperator;
    },
    isBasicNeedsSpecialist: function() {
      return this.$store.getters.isBasicNeedsSpecialist;
    },
    user: function() {
      //console.log(`status: ${this.$store.state.status}, user: ${this.$store.state.user?.firstName}`);
      if (this.$store.state.status === "success") {
        return this.$store.state.user;
      } else {
        return null;
      }
    },
    is_impersonating: function() {
      return localStorage.getItem("uwillImpersonation") == "true";
    },
    isStaging: function() {
      return process.env.NODE_ENV === "staging";
    },
    isDemo: function() {
      return process.env.NODE_ENV === "demo";
    },
    showUcollaborateBanner: function() {
      return (
        this.$route.name === "Collaborate" &&
        this.$store.getters.ucollabRedesignToggle
      );
    },
    locale: function() {
      if (this.$store?.state?.user?.locale) {
        return this.$store.state.user.locale;
      } else if (this.$i18n?.locale) {
        return this.$i18n.locale;
      } else if (this.user?.locale) {
        return this.user.locale;
      } else {
        return "en-US";
      }
    },
    can_message: function() {
      const products = this.user?.products;
      const productEligible = !!(
        (this.user?.provider ||
          products?.includes("Umatch") ||
          products?.includes("Uthrive")) &&
        !this.isUriseOnly
      );
      return (
        this.isLoggedIn &&
        this.isConfirmed &&
        !this.isIncomplete &&
        this.$store.getters.messagingEnabled &&
        productEligible
      );
    }
  },
  watch: {
    amplitudeIdentityKey: {
      immediate: true,
      flush: "sync",
      handler(identityKey) {
        if (identityKey) {
          // UI events also run in staging, so identify there before child events.
          identifyAmplitudeUser(this.$amplitude, this.$store.state.user);
        }
      }
    },
    isLoggedIn: function(newVal) {
      if (newVal) {
        this.$store.dispatch("get_session_settings");
        this.$store.dispatch("get_optional_settings");
      }
    },
    snackbar: function(newValue) {
      if (newValue) {
        this.$nextTick(() => {
          this.$refs.snackbarContent.focus(); // Move focus to the snackbar
        });
      }
    },
    can_message: function(newValue) {
      // `inbox.state.isOpen` lives outside this component's lifecycle, so it
      // must be closed explicitly (e.g., by `logout`). Unmounting `InboxDrawer`
      // also won't reset it for the same reason.
      if (!newValue) {
        inbox.close();
      }
    }
  },
  created: function() {
    // TODO: Uncomment and resolve Vue 3 interceptors issue
    // this.$http.interceptors.response.use(undefined, function(err) {
    //   return new Promise(function() {
    //     if (err.status === 401 && err.config && !err.config.__isRetryRequest) {
    //       this.$store.dispatch("logout");
    //     }
    //     throw err;
    //   });
    // });

    if (this.isLoggedIn) {
      this.$store.dispatch("get_session_settings");
      this.$store.dispatch("get_optional_settings");
    }

    if (window.self !== window.top) {
      try {
        window.top.location = window.self.location;
      } catch (e) {
        console.warn(
          "Clickjacking protection: Unable to break out of iframe.",
          e
        );
        document.body.innerHTML = "<h3>Sorry, this is not allowed</h3>";
      }
    }
  },
  beforeCreate() {
    this.$store.commit("DASHBOARD");
  },
  mounted: function() {
    if (process.env.NODE_ENV === "staging") {
      let auth = localStorage.getItem("uwill_staging_authorized");
      var redirect;
      if (parseInt(localStorage.getItem("uwill_staging_authorized")) > 0) {
        let now = new Date();
        let permitted = auth > now.valueOf();
        if (permitted) {
          redirect = false;
        } else {
          redirect = true;
          localStorage.removeItem("uwill_staging_authorized");
        }
      } else {
        redirect = auth == "true" ? false : true;
      }
      if (redirect) {
        this.$router.push("/doc/staging");
      }
    } else {
      if (this.isLoggedIn) {
        this.$amplitude.getInstance().logEvent("Page loaded", {
          url: document.location.pathname
        });
      }
    }
  },
  methods: {
    // Vue is pulling focus back to the menu icon so we need to to focus on first menu item when menu opened
    focusFirstMenuItem: function() {
      const el = document.querySelector(".account-menu .ada-menu-item");

      el?.focus();
    },
    logout: function() {
      this.closeMenu();
      // Persist an explicit-logout flash message so the login page shows it
      tokenService.persistFlashPayload({
        message_type: "success",
        text: "You have successfully logged out."
      });
      this.$store.dispatch("logout").then(() => {
        this.$router.push("/login");
      });
    },
    closeMenu: function() {
      this.account_menu = false;
    },
    login() {
      this.$router.push("/login");
    }
  }
};
</script>
